import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { SiteIllustration } from '../components/ui/SiteIllustration';
import SafeIcon from '../common/SafeIcon';
import { 
  FiActivity, FiAlertTriangle, FiTruck, FiShield, 
  FiArrowRight, FiUsers, FiClock, FiMaximize2 
} from 'react-icons/fi';
import { METRICS, RECENT_ACTIVITY, PROJECTS, DELIVERIES } from '../data/mockData';

const StatCard = ({ title, value, icon, trend, isWarning, color = 'cyan' }) => (
  <Card className="relative overflow-hidden group border-slate-800/40 min-h-[120px]">
    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
      <SafeIcon icon={icon} className="text-6xl" />
    </div>
    <div className="relative z-10 flex flex-col h-full justify-between">
      <div className="flex justify-between items-start">
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{title}</span>
        <div className={`p-2 rounded-sm ${isWarning ? 'bg-yellow-950/30 text-yellow-500 shadow-[0_0_15px_-5px_rgba(234,179,8,0.3)]' : `bg-${color}-950/30 text-${color}-500 shadow-[0_0_15px_-5px_rgba(6,182,212,0.3)]`}`}>
          <SafeIcon icon={icon} className="text-sm" />
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-100 flex items-baseline gap-2">
          {value}
          {trend && <span className="text-[10px] font-mono text-emerald-400">{trend}</span>}
        </div>
      </div>
    </div>
  </Card>
);

export const Dashboard = () => {
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15,23,42,0.9)', borderColor: '#1e293b', textStyle: { color: '#cbd5e1' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'], axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8', fontSize: 10, fontFamily: 'monospace' } },
    yAxis: { type: 'value', splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } }, axisLabel: { color: '#94a3b8', fontSize: 10, fontFamily: 'monospace' } },
    series: [{ 
      name: 'Site Activity', type: 'line', smooth: true, data: [45, 78, 92, 110, 85, 42, 20],
      itemStyle: { color: '#06b6d4' },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(6,182,212,0.3)' }, { offset: 1, color: 'rgba(6,182,212,0.0)' }] } } 
    }]
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-[1600px] mx-auto">
      {/* Branding Header Area */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-800/50 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-4 bg-cyan-500"></div>
            <h1 className="text-2xl font-bold text-slate-100 uppercase tracking-tighter">Command Centre / Alpha</h1>
          </div>
          <p className="text-slate-500 text-[10px] font-mono uppercase tracking-[0.2em]">Operational Intelligence Terminal • Sector 01</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="cyan" className="animate-pulse">Live Site Sync</Badge>
          <div className="h-8 w-px bg-slate-800 mx-2"></div>
          <button className="bg-cyan-600 hover:bg-cyan-500 text-slate-950 px-4 py-2 rounded-sm text-[10px] font-bold uppercase tracking-widest transition-all">Mobilize Team</button>
        </div>
      </div>

      {/* Primary Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Crew" value={METRICS.manHoursToday} icon={FiUsers} trend="+12%" />
        <StatCard title="Site Integrity" value={`${METRICS.safetyScore}%`} icon={FiShield} trend="Nominal" />
        <StatCard title="Active Signals" value={METRICS.criticalRFIs} icon={FiAlertTriangle} isWarning />
        <StatCard title="Logic Uptime" value={`${METRICS.equipmentUptime}%`} icon={FiActivity} color="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Structural Telemetry (BIM Activity)" action={<SafeIcon icon={FiMaximize2} className="text-slate-600 cursor-pointer"/>}>
            <div className="h-[320px]">
              <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
            </div>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Structural Visualization">
              <SiteIllustration type="structure" />
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-slate-500">Node Sync</span>
                  <span className="text-cyan-400">92%</span>
                </div>
                <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                  <div className="bg-cyan-500 h-full w-[92%]"></div>
                </div>
              </div>
            </Card>
            <Card title="Manpower Density">
              <SiteIllustration type="manpower" />
              <div className="mt-4 flex justify-around text-center">
                <div>
                  <p className="text-[10px] text-slate-500 font-mono uppercase">Sector A</p>
                  <p className="text-sm font-bold text-slate-200">142</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 font-mono uppercase">Sector B</p>
                  <p className="text-sm font-bold text-slate-200">89</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="space-y-6">
          <Card title="Active Sectors" className="h-full">
            <div className="space-y-3">
              {PROJECTS.map((project) => (
                <div key={project.id} className="p-3 bg-slate-900/50 border border-slate-800/30 rounded-sm hover:border-cyan-500/20 group transition-all cursor-pointer">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-slate-200 group-hover:text-cyan-400">{project.name}</span>
                    <Badge variant={project.health === 'Warning' ? 'yellow' : 'cyan'} className="text-[9px]">{project.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-[9px] font-mono text-slate-500 mb-1">
                    <span>{project.site}</span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                    <div className={`h-full ${project.health === 'Warning' ? 'bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]' : 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]'}`} style={{ width: `${project.progress}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-6 py-3 border border-slate-800 text-[9px] text-slate-500 hover:text-cyan-400 hover:border-cyan-500/50 uppercase tracking-widest flex items-center justify-center gap-2 transition-all">
              Initialize Sector Map <SafeIcon icon={FiArrowRight} />
            </button>
          </Card>

          <Card title="System Log Feed">
            <div className="space-y-4">
              {RECENT_ACTIVITY.slice(0, 5).map((log) => (
                <div key={log.id} className="flex gap-3 text-[11px] border-l border-slate-800 pl-3">
                  <div className="text-slate-600 font-mono shrink-0">{log.time}</div>
                  <div>
                    <span className={`font-bold mr-2 ${log.isWarning ? 'text-yellow-500' : 'text-cyan-500'}`}>[{log.type}]</span>
                    <span className="text-slate-400">{log.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </motion.div>
  );
};